import { useMemo } from 'react';
import { useUiStore } from '@/store/uiStore';
import { featureFlags } from '@/config/featureFlags';
import { TEXAS_COUNTIES, TEXAS_CITIES } from '@/geo/texasCounties';

/** Zoom level to fly to when a specific county is picked from the dropdown. */
const COUNTY_JUMP_ZOOM = 9;
/** Zoom level to fly to when a specific city is picked from the dropdown. */
const CITY_JUMP_ZOOM = 11;

export function MapControls() {
  const geoLevel = useUiStore((s) => s.geoLevel);
  const setGeoLevel = useUiStore((s) => s.setGeoLevel);
  const autoGeoLevel = useUiStore((s) => s.autoGeoLevel);
  const setAutoGeoLevel = useUiStore((s) => s.setAutoGeoLevel);
  const selectGeo = useUiStore((s) => s.selectGeo);
  const requestFlyTo = useUiStore((s) => s.requestFlyTo);

  const sortedCounties = useMemo(
    () => [...TEXAS_COUNTIES].sort((a, b) => a.name.localeCompare(b.name)),
    [],
  );
  const sortedCities = useMemo(
    () => [...TEXAS_CITIES].sort((a, b) => a.name.localeCompare(b.name)),
    [],
  );

  const handleCountyJump = (geoId: string) => {
    if (!geoId) return;
    selectGeo(geoId);
    const county = TEXAS_COUNTIES.find((c) => c.geoId === geoId);
    if (county?.lat != null && county?.lon != null) {
      requestFlyTo({ lat: county.lat, lon: county.lon, zoom: COUNTY_JUMP_ZOOM });
    }
  };

  const handleCityJump = (cityGeoId: string) => {
    if (!cityGeoId) return;
    const city = TEXAS_CITIES.find((c) => c.geoId === cityGeoId);
    if (!city) return;
    // Cities don't carry independent market data in Phase 1 -- selecting one
    // opens its parent county's record, same as clicking the city marker.
    selectGeo(city.countyFips);
    requestFlyTo({ lat: city.lat, lon: city.lon, zoom: CITY_JUMP_ZOOM });
  };

  return (
    <div className="map-controls">
      <div>
        <label className="map-controls__label" htmlFor="geo-level-select">
          Layer
        </label>
        <select
          id="geo-level-select"
          value={geoLevel}
          onChange={(e) => setGeoLevel(e.target.value as typeof geoLevel)}
        >
          <option value="county">County</option>
          <option value="city">City (approximate)</option>
          {featureFlags.ENABLE_ZIP_LAYER && <option value="zip">ZIP</option>}
          {featureFlags.ENABLE_ZILLOW && <option value="neighborhood">Neighborhood (Houston/DFW/Austin/San Antonio)</option>}
        </select>
      </div>

      {(featureFlags.ENABLE_ZIP_LAYER || featureFlags.ENABLE_ZILLOW) && (
        <div>
          <label className="map-controls__label" htmlFor="auto-geo-level-toggle">
            <input
              id="auto-geo-level-toggle"
              type="checkbox"
              checked={autoGeoLevel}
              onChange={(e) => setAutoGeoLevel(e.target.checked)}
            />
            {' '}Auto (zoom in for ZIPs / neighborhoods)
          </label>
        </div>
      )}

      <div>
        <label className="map-controls__label" htmlFor="county-jump-select">
          Jump to county
        </label>
        <select
          id="county-jump-select"
          value=""
          onChange={(e) => handleCountyJump(e.target.value)}
        >
          <option value="">Select a county…</option>
          {sortedCounties.map((county) => (
            <option key={county.geoId} value={county.geoId}>
              {county.name}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label className="map-controls__label" htmlFor="city-jump-select">
          Jump to city
        </label>
        <select
          id="city-jump-select"
          value=""
          onChange={(e) => handleCityJump(e.target.value)}
        >
          <option value="">Select a city…</option>
          {sortedCities.map((city) => (
            <option key={city.geoId} value={city.geoId}>
              {city.name}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
}
